import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Calendar, Clock, User, MessageSquare, ArrowLeft } from 'lucide-react';
import { formatPrice, formatDuration, getImageUrl } from '../utils/helpers';
import { getDisponibilidad, getHorarioSemanal } from '../api/agenda';
import { useNotify } from './NotificationProvider';
import DatePicker from './DatePicker';

// Estilos especiales
const bookingStyles = `
  .booking-title {
    color: white !important;
  }
`;

// Inyectar estilos
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = bookingStyles;
  document.head.appendChild(style);
}

const DEFAULT_DURATION_MIN = 120; // duración por defecto si el producto no tiene una
const SLOT_STEP_MIN = 60;       // slots visibles cada 1 hora

const reservationDuration = (product) => {
  const d = product?.Duracion != null ? Number(product.Duracion) : null;
  return Number.isInteger(d) && d > 0 ? d : DEFAULT_DURATION_MIN;
};

// Utilidades de tiempo en minutos
const toMinutes = (hhmmss) => {
  if (!hhmmss) return null;
  const parts = String(hhmmss).trim().split(':').map(p => parseInt(p, 10));
  if (parts.length < 2 || parts.some(isNaN)) return null;
  const hh = parts[0];
  const mm = parts[1];
  return hh * 60 + mm;
};

const fmtHHMM = (minutes) => {
  if (minutes == null || Number.isNaN(minutes)) return null;
  const hh = Math.floor(minutes / 60);
  const mm = minutes % 60;
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
};

const sortSlots = (slots = []) => {
  const filtered = (slots || []).filter(Boolean);
  const unique = Array.from(new Set(filtered));
  return unique.sort((a, b) => (toMinutes(a) ?? 0) - (toMinutes(b) ?? 0));
};

const buildSlots = (startStr, endStr, step) => {
  const startMin = toMinutes(startStr);
  const endMin = toMinutes(endStr);
  if (startMin === null || endMin === null || startMin >= endMin) return [];
  const s = Math.max(1, Number(step) || 30);
  const out = [];
  for (let cur = startMin; cur + s <= endMin; cur += s) {
    out.push(fmtHHMM(cur));
  }
  return out;
};

const BookingForm = ({ product, onSubmit, onBack, user }) => {
  const duracionReserva = reservationDuration(product);
  const [formData, setFormData] = useState({ fecha: '', hora: '', manicuristaId: '', notas: '' });
  const [errors, setErrors] = useState({});
  const [availableSlots, setAvailableSlots] = useState([]);
  const [allSlots, setAllSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [weeklySchedule, setWeeklySchedule] = useState([]);
  const [occupiedSlots, setOccupiedSlots] = useState([]);   // solo reservas reales
  const [manicuristasPorSlot, setManicuristasPorSlot] = useState({});
  const [reloadKey, setReloadKey] = useState(0);

  const prevSlotsRef = useRef([]);
  const prevDateRef = useRef('');
  const { success: notifySuccess, error: notifyError, info: notifyInfo } = useNotify();

  const getSlotManicuristas = (slot) => {
    const key = slot ? String(slot).slice(0, 5) : '';
    const list = manicuristasPorSlot[key];
    return Array.isArray(list) ? list : [];
  };

  const handleInputChange = (field, value) => {
    if (field === 'fecha') {
      const active = isDayActive(value);
      if (!active) {
        setErrors(prev => ({ ...prev, fecha: 'Día no disponible' }));
        setFormData(prev => ({ ...prev, fecha: '', hora: '', manicuristaId: '' }));
        setAvailableSlots([]);
        setManicuristasPorSlot({});
        return;
      }
    }
    setFormData(prev => {
      const next = { ...prev, [field]: value };
      if (field === 'fecha') {
        next.hora = '';
        next.manicuristaId = '';
      }
      if (field === 'hora') {
        next.manicuristaId = '';
      }
      return next;
    });
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
    if (field === 'hora' && !value) {
      // limpiar solo selección de hora
      setFormData(prev => ({ ...prev, hora: '', manicuristaId: '' }));
    }
  };

  const today = new Date().toISOString().slice(0, 10);
  const computeWeekEndISO = () => {
    const now = new Date();
    const utc = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
    const dow = (utc.getUTCDay() + 6) % 7;
    const weekStart = new Date(Date.UTC(utc.getUTCFullYear(), utc.getUTCMonth(), utc.getUTCDate() - dow));
    const weekEnd = new Date(Date.UTC(weekStart.getUTCFullYear(), weekStart.getUTCMonth(), weekStart.getUTCDate() + 6));
    return weekEnd.toISOString().slice(0,10);
  };
  const weekEndISO = computeWeekEndISO(); // por si lo necesitas más adelante

  useEffect(() => {
    const loadSchedule = async () => {
      try {
        const data = await getHorarioSemanal();
        setWeeklySchedule(Array.isArray(data) ? data : []);
      } catch (e) {
        console.warn('No se pudo cargar el horario semanal:', e?.message);
      }
    };
    loadSchedule();
  }, []);

  const isDayActive = (dateStr) => {
    if (!dateStr || !weeklySchedule?.length) return true;
    try {
      const [Y, M, D] = String(dateStr).split('-').map(Number);
      const utc = new Date(Date.UTC(Y, (M || 1) - 1, D || 1));
      const dow = utc.getUTCDay();
      const dia = dow === 0 ? 7 : dow;
      const row = weeklySchedule.find(r => Number(r.DiaSemana) === dia);
      if (!row) return true;
      const v = row.Activo;
      return v === true || v === 1 || v === '1' || String(v).toLowerCase() === 'true';
    } catch {
      return true;
    }
  };

  useEffect(() => {
    const load = async () => {
      if (!formData.fecha || !product?.Id) {
        setAvailableSlots([]);
        setOccupiedSlots([]);
        setAllSlots([]);
        setManicuristasPorSlot({});
        return;
      }

      try {
        setLoadingSlots(true);
        const res = await getDisponibilidad(formData.fecha, product.Id);

        let slots = [];
        let fullRange = [];
        const inicio = res?.Inicio || res?.inicio || res?.start || null;
        const fin = res?.Fin || res?.fin || res?.end || null;
        const slotSpan = parseInt(res?.StepMinutos, 10) || SLOT_STEP_MIN;
        const activo = res?.Activo ?? res?.activo;

        if (activo === false) {
          slots = [];
          fullRange = [];
        } else if (inicio && fin) {
          if (Array.isArray(res?.allSlots) && res.allSlots.length) {
            fullRange = res.allSlots.map(s => String(s).slice(0,5));
          } else {
            fullRange = buildSlots(inicio, fin, slotSpan);
          }

          if (Array.isArray(res?.slots) && res.slots.length) {
            slots = (res.slots || []).filter(Boolean).map(s => {
              const v = String(s).trim();
              const [h,m] = v.split(':');
              if (h != null && m != null) return `${String(parseInt(h,10)).padStart(2,'0')}:${String(parseInt(m,10)).padStart(2,'0')}`;
              return v.slice(0,5);
            });
          } else {
            slots = fullRange;
          }
        } else if (Array.isArray(res?.slots) && res.slots.length) {
          slots = (res.slots || []).filter(Boolean).map(s => {
            const v = String(s).trim();
            const [h,m] = v.split(':');
            if (h != null && m != null) return `${String(parseInt(h,10)).padStart(2,'0')}:${String(parseInt(m,10)).padStart(2,'0')}`;
            return v.slice(0,5);
          });
          fullRange = [];
        }

        // Normalizar ocupados/bloqueados del backend
        const ocupados = Array.isArray(res?.ocupados) ? res.ocupados.map(s => String(s).slice(0,5)) : [];
        const bloqueados = Array.isArray(res?.bloqueados) ? res.bloqueados.map(s => String(s).slice(0,5)) : [];
        const blockedSlots = sortSlots([...ocupados, ...bloqueados]);
        const slotManicuristas = Object.entries(res?.manicuristasPorSlot || {}).reduce((acc, [slot, list]) => {
          const key = String(slot).slice(0, 5);
          acc[key] = Array.isArray(list)
            ? list.map(item => ({
                Id: Number(item.Id ?? item.id),
                Nombre: item.Nombre || item.nombre || 'Manicurista'
              })).filter(item => Number.isInteger(item.Id))
            : [];
          return acc;
        }, {});

        let unique = sortSlots(slots);
        const uniqueFull = sortSlots(fullRange);

        // quitar todo lo bloqueado (real o temporal)
        unique = unique.filter(s => !blockedSlots.includes(s));

        if (prevDateRef.current === formData.fecha && formData.hora) {
          const wasThere = (prevSlotsRef.current || []).includes(formData.hora);
          const isNowMissing = !unique.includes(formData.hora);
          if (wasThere && isNowMissing) {
            notifyInfo(`El horario ${formData.hora} se acaba de ocupar, elige otro.`, { duration: 5000 });
          }
        }

        setAvailableSlots(unique);
        setAllSlots(uniqueFull);
        setOccupiedSlots(sortSlots(ocupados)); // solo reservas reales
        setManicuristasPorSlot(slotManicuristas);

        if (formData.hora && !unique.includes(formData.hora)) {
          if (prevDateRef.current === formData.fecha && (prevSlotsRef.current || []).includes(formData.hora)) {
            notifyInfo('Ese horario fue tomado hace instantes. Por favor, elige otro.', { duration: 5000 });
          }
          setFormData(prev => ({ ...prev, hora: '', manicuristaId: '' }));
        }

        prevSlotsRef.current = unique;
        prevDateRef.current = formData.fecha;
      } catch (e) {
        console.error('Error cargando disponibilidad:', e);
        setAvailableSlots([]);
        setAllSlots([]);
        setOccupiedSlots([]);
        setManicuristasPorSlot({});
      } finally {
        setLoadingSlots(false);
      }
    };

    load();
  }, [formData.fecha, product?.Id, reloadKey]);

  useEffect(() => {
    if (!formData.hora) return;
    const disponibles = getSlotManicuristas(formData.hora);
    setFormData(prev => {
      const currentStillAvailable = disponibles.some(item => String(item.Id) === String(prev.manicuristaId));
      if (currentStillAvailable) return prev;
      return {
        ...prev,
        manicuristaId: disponibles.length === 1 ? String(disponibles[0].Id) : ''
      };
    });
  }, [formData.hora, manicuristasPorSlot]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const fechaISO = formData.fecha ? String(formData.fecha).slice(0, 10) : null;
    const horaHM = formData.hora ? String(formData.hora).slice(0, 5) : null;
    const manicuristaId = Number(formData.manicuristaId);

    if (!fechaISO || !horaHM) {
      setErrors(prev => ({
        ...prev,
        ...(fechaISO ? {} : { fecha: 'La fecha es requerida' }),
        ...(horaHM ? {} : { hora: 'La hora es requerida' })
      }));
      notifyError('Selecciona fecha y hora válidas', { duration: 4000 });
      return;
    }

    const manicuristasDisponibles = getSlotManicuristas(horaHM);
    const manicuristaSeleccionada = manicuristasDisponibles.find(item => item.Id === manicuristaId);
    if (!Number.isInteger(manicuristaId) || !manicuristaSeleccionada) {
      setErrors(prev => ({ ...prev, manicuristaId: 'No hay manicurista disponible para este horario' }));
      notifyError('No hay manicurista disponible para este horario', { duration: 4500 });
      return;
    }

    if (availableSlots.length && !availableSlots.includes(horaHM)) {
      setErrors(prev => ({ ...prev, hora: 'La hora ya no está disponible' }));
      notifyError('La hora ya no está disponible, elige otra', { duration: 4500 });
      return;
    }

    try {
      notifyInfo('Enviando reserva...', { duration: 2500 });

      // limpiar selección local
      setFormData(prev => ({ ...prev, hora: '', manicuristaId: '' }));

      await onSubmit({
        usuario: user?.nombreUsuario || user?.username || user?.nombreCompleto || '',
        fecha: fechaISO,
        hora: horaHM,
        manicuristaId,
        manicuristaNombre: product?.ManicuristaNombre || manicuristaSeleccionada?.Nombre || '',
        notas: formData.notas,
        productId: product.Id
      });

      setReloadKey(k => k + 1);

      setTimeout(() => {
        setReloadKey(k => k + 1);
      }, 1000);

      notifySuccess('Reserva confirmada! Refrescando disponibilidad...', { duration: 3000 });
    } catch (err) {
      setFormData(prev => ({ ...prev, hora: horaHM, manicuristaId: String(manicuristaId) }));
    }
  };

  // rango bloqueado por selección actual (visual)
  const isInSelectedRange = (slot) => {
    if (!formData.hora) return false;
    const start = toMinutes(formData.hora);
    const cur = toMinutes(slot);
    if (start === null || cur === null) return false;
    // slots posteriores dentro de las 2h
    return cur > start && cur < start + duracionReserva;
  };

  const handleSlotClick = (slotStr) => {
    const disponibles = getSlotManicuristas(slotStr);
    const asignada = product?.ManicuristaId ? String(product.ManicuristaId) : null;
    const manicuristaId = asignada && disponibles.some(m => String(m.Id) === asignada)
      ? asignada
      : disponibles.length > 0 ? String(disponibles[0].Id) : '';
    setFormData(prev => ({
      ...prev,
      hora: slotStr,
      manicuristaId
    }));
    if (errors.hora) setErrors(prev => ({ ...prev, hora: '' }));
    if (errors.manicuristaId) setErrors(prev => ({ ...prev, manicuristaId: '' }));
  };

  const baseSlots = sortSlots(allSlots.length ? allSlots : availableSlots);

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.5 }}
      className="bg-white/90 backdrop-blur-xl rounded-3xl shadow-xl overflow-hidden"
    >
      <div className="bg-gradient-to-r from-pink-500 to-purple-600 p-6 text-white">
        <div className="flex items-center gap-4 mb-6">
          <motion.button
            onClick={onBack}
            className="p-2 rounded-xl bg-white/20 hover:bg-white/30 transition-colors"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <ArrowLeft className="w-5 h-5" />
          </motion.button>
          <h2 className="text-2xl font-bold booking-title">Reservar Servicio</h2>
        </div>
        <div className="bg-white/20 rounded-2xl p-4 flex flex-col md:flex-row gap-4 md:gap-6">
          {product?.UrlImagen && (
            <img
              src={getImageUrl(product.UrlImagen)}
              alt={product?.Nombre || 'Servicio'}
              className="w-24 h-24 md:w-28 md:h-28 rounded-xl object-cover shadow-lg ring-2 ring-white/30"
            />
          )}
          <div className="flex-1 flex flex-col justify-center gap-2 text-sm md:text-base">
            <div className="flex items-center gap-2">
              <span className="font-semibold opacity-90">Nombre del servicio:</span>
              <span className="font-bold">{product?.Nombre || 'Servicio'}</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-gray-200" />
              <span className="font-semibold opacity-90">Duración del servicio:</span>
              <span>{formatDuration(duracionReserva)}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-semibold opacity-90">Valor del servicio:</span>
              <span className="font-bold text-lg">{formatPrice(product.Precio)}</span>
            </div>
            <div className="flex items-center gap-2 mt-2">
              <User className="w-4 h-4 text-gray-200" />
              <span className="font-semibold opacity-90">Cliente:</span>
              <span className="text-white">{user?.nombreCompleto || user?.nombreUsuario || user?.username || 'Usuario'}</span>
            </div>
            {product?.ManicuristaNombre && (
              <div className="flex items-center gap-2 mt-2">
                <span className="font-semibold opacity-90">Manicurista:</span>
                <span className="font-bold text-white">{product.ManicuristaNombre}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="p-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-gray-700 font-medium mb-2 flex items-center gap-2">
              <Calendar className="w-4 h-4 text-gray-500" />
              Fecha a reservar el servicio
            </label>
            <DatePicker
              value={formData.fecha}
              onChange={(d) => handleInputChange('fecha', d)}
              isDayDisabled={(d) => !isDayActive(d)}
              minDate={today}
              view="week"
            />
            {errors.fecha && <p className="text-red-500 text-sm mt-1">{errors.fecha}</p>}
          </div>
        </div>

        {formData.fecha && (
          <div>
            <label className="block text-gray-700 font-medium mb-2 flex items-center gap-2">
              <Clock className="w-4 h-4 text-gray-500" />
              Horas disponibles para el servicio
              <span className="text-xs text-gray-500 ml-2">
                (Cada reserva ocupa {Math.ceil(duracionReserva / 60)} horas)
              </span>
            </label>

            {loadingSlots ? (
              <p className="text-gray-500 mb-3">Cargando horarios...</p>
            ) : (
              <>
                {formData.fecha && !isDayActive(formData.fecha) && (
                  <div className="text-center text-red-600 font-medium mb-3">
                    Este día no está disponible.
                  </div>
                )}
                {availableSlots.length === 0 ? (
                  <div className="text-center text-gray-600 mb-3">
                    No hay horarios disponibles para este día y servicio.
                  </div>
                ) : (
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2 mb-3">
                    {baseSlots.map((slot, idx) => {
                      const isAvailable = availableSlots.includes(slot);
                      const isReserved = occupiedSlots.includes(slot);
                      const isSelected = formData.hora === slot;
                      const inSelectedRange = isInSelectedRange(slot);

                      let bgClass = 'bg-gray-100';
                      let textClass = 'text-gray-700';
                      let cursorClass = 'cursor-pointer';

                      if (!isAvailable) {
                        bgClass = 'bg-red-100';
                        textClass = 'text-red-700';
                        cursorClass = 'cursor-not-allowed';
                      } else if (isSelected) {
                        bgClass = 'bg-gradient-to-r from-pink-500 to-purple-600';
                        textClass = 'text-white';
                      } else if (inSelectedRange) {
                        bgClass = 'bg-yellow-100';
                        textClass = 'text-yellow-700';
                        cursorClass = 'cursor-not-allowed';
                      } else {
                        bgClass = 'bg-gray-100 hover:bg-gray-200';
                      }

                      return (
                        <motion.button
                          key={`${slot}-${idx}`}
                          type="button"
                          onClick={() => isAvailable && !inSelectedRange && handleSlotClick(slot)}
                          className={`py-2 px-3 rounded-lg font-medium transition-all ${bgClass} ${textClass} ${cursorClass}`}
                          disabled={!isAvailable || inSelectedRange}
                          whileHover={{ scale: isAvailable && !inSelectedRange ? 1.05 : 1 }}
                          whileTap={{ scale: isAvailable && !inSelectedRange ? 0.95 : 1 }}
                        >
                          {slot}
                          {isReserved && !isAvailable && (
                            <div className="text-xs mt-1">Ocupado</div>
                          )}
                        </motion.button>
                      );
                    })}
                  </div>
                )}
              </>
            )}

            {errors.hora && <p className="text-red-500 text-sm mt-1">{errors.hora}</p>}

            {/* Leyenda de colores */}
            <div className="flex flex-wrap gap-4 text-xs text-gray-600 mt-4">
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 bg-gradient-to-r from-pink-500 to-purple-600 rounded"></div>
                <span>Seleccionado</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 bg-gray-100 rounded"></div>
                <span>Disponible</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 bg-red-100 rounded"></div>
                <span>Ocupado ({Math.ceil(duracionReserva / 60)}h)</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 bg-yellow-100 rounded"></div>
                <span>Bloqueado por selección</span>
              </div>
            </div>
          </div>
        )}

        <div>
          <label className="block text-gray-700 font-medium mb-2 flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-gray-500" />
            Notas Adicionales (Opcional)
          </label>
          <textarea
            value={formData.notas}
            onChange={(e) => handleInputChange('notas', e.target.value)}
            rows={3}
            className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-pink-500 focus:outline-none resize-none"
            placeholder="Alguna preferencia especial o comentario..."
          />
        </div>

        <div className="flex flex-row gap-3">
          <motion.button
            type="button"
            onClick={onBack}
            className="flex-1 py-2 px-4 bg-gray-200 text-gray-800 font-semibold rounded-lg hover:bg-gray-300 transition-all duration-300"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            Cancelar
          </motion.button>
          <motion.button
            type="submit"
            className="flex-1 py-2 px-4 bg-gradient-to-r from-pink-500 to-purple-600 text-white font-bold rounded-lg shadow hover:shadow-md transition-all duration-300"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            Confirmar
          </motion.button>
        </div>
      </form>
    </motion.div>
  );
};

export default BookingForm;
